import React, { Component } from "react";
import { FormGroup } from "reactstrap";
import Isvg from 'react-inlinesvg';
import errorIcon from "../../../assets/svg/required.svg";

const keys = {
  up: "ArrowUp",
  down: "ArrowDown",
  enter: "Enter",
  esc: "Escape",
};

const DEBOUNCE_TIMER_DELAY_MS = 300;
const SHOW_LOADING_ICON_DELAY_MS = 100;

class ComboBoxField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      options: props.options || [],
      showOptions: false,
      debouncedValue: props.value || "",
      index: 0,
      scroll: false,
      loading: false,
    };

    this.timer = null;
    this.loadingIconTimer = null;
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener("keydown", this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onKeyDown);
    clearTimeout(this.timer);
    clearTimeout(this.loadingIconTimer);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState({ debouncedValue: this.props.value || "" });
    }

    if (prevProps.options !== this.props.options) {
      this.setState({ options: this.props.options || [], index: 0, scroll: true });
    }

    if (this.state.scroll && prevState.index !== this.state.index) {
      const { index, options } = this.state;
      const { id } = this.props;
      if (options[index]) {
        const el = document.getElementById(`${id}_option_${this.getOptionValue(options[index])}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      this.setState({ scroll: false });
    }
  }

  getOptionLabel(option) {
    if (!option) return "";
    return typeof option === "string" ? option : option.label || "";
  }

  getOptionValue(option) {
    if (!option) return "";
    return typeof option === "string" ? option : option.value ?? "";
  }

  fetchOptions(value) {
    const { optionsFetch } = this.props;
    if (!optionsFetch?.url || !value?.trim()) return;

    clearTimeout(this.loadingTimer);
    this.loadingTimer = setTimeout(() => {
      this.setState({ loading: true });
    }, SHOW_LOADING_ICON_DELAY_MS);

    fetch(optionsFetch.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        entries: 20,
        page: 1,
        filter: value.trim(),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        let items = data.items || [];
        if (optionsFetch.transformer && typeof optionsFetch.transformer === "function") {
          items = optionsFetch.transformer(items);
        }

        clearTimeout(this.loadingTimer);
        this.setState({ options: items, index: 0, scroll: true, loading: false });
      })
      .catch(() => {
        clearTimeout(this.loadingTimer);
        this.setState({ options: [], index: 0, loading: false });
      });
  }

  onKeyDown = (e) => {
    const { showOptions, index, options } = this.state;
    if (!showOptions || options.length === 0) return;

    if (![keys.up, keys.down, keys.enter, keys.esc].includes(e.key)) return;

    e.preventDefault();
    e.stopPropagation();

    switch (e.key) {
      case keys.down:
        this.setState({
          index: index + 1 >= options.length ? 0 : index + 1,
          scroll: true,
        });
        break;
      case keys.up:
        this.setState({
          index: index - 1 < 0 ? options.length - 1 : index - 1,
          scroll: true,
        });
        break;
      case keys.enter:
        if (options[index]) this.handleOptionClick(options[index]);
        else this.setState({ showOptions: false });
        break;
      case keys.esc:
        this.setState({ showOptions: false, index: 0 });
        break;
      default:
        break;
    }
  };

  handleInputChange = (e) => {
    const typedValue = e.target.value;
    this.setState({ debouncedValue: typedValue, index: 0 });

    this.props.onChange?.(typedValue);

    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (typedValue?.trim()) this.fetchOptions(typedValue);
      else this.setState({ options: [], index: 0 });
    }, DEBOUNCE_TIMER_DELAY_MS);
  };

  handleFocus = () => {
    this.setState({ showOptions: true, index: 0, scroll: true });
    if (this.state.debouncedValue?.trim()) this.fetchOptions(this.state.debouncedValue);
    this.props.onFocus?.();
  };

  handleBlur = () => {
    setTimeout(() => {
      this.setState({ showOptions: false, index: 0 });
      this.props.onChange?.(this.state.debouncedValue);
      this.props.onBlur?.();
    }, 150);
  };

  handleOptionClick = (option) => {
    const label = this.getOptionLabel(option);
    const value = this.getOptionValue(option);

    this.setState({ debouncedValue: label, showOptions: false, index: 0 });
    this.props.onChange?.(value);
  };

  render() {
    const { options, showOptions, index, loading } = this.state;
    const { id, name, placeholder, label, error, haveValidation } = this.props;

    return (
      <FormGroup>
        {label && (
          <label className={`form-group-label-wrap col-form-label-sm ${error ? "error" : ""}`}>
            {label}
            {haveValidation && <span className="required-label-span">*</span>}
          </label>
        )}

        <div className="combobox-wrap">
          <div className="combobox-input-wrap">
            <input
              className="combobox-input"
              type="text"
              id={id}
              name={name}
              value={this.state.debouncedValue || ""}
              placeholder={placeholder}
              onChange={this.handleInputChange}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              invalid={!!error}
            />

            {error && <Isvg src={errorIcon} className="combobox-input-icon combobox-error-icon"></Isvg>}
            {(loading && !error) && <div className="combobox-input-icon combobox-loading-icon"></div>}
          </div>

          {showOptions && options.length > 0 && (
            <ul className="combobox-ul">
              {options.map((option, i) => {
                const labelText = this.getOptionLabel(option);
                const valueText = this.getOptionValue(option);
                const isActive = i === index;

                return (
                  <li
                    key={valueText}
                    id={`${id}_option_${valueText}`}
                    role="option"
                    className={`combobox-li ${isActive ? "active" : ""}`}
                    onMouseEnter={() => this.setState({ index: i, scroll: false })}
                    onClick={() => this.handleOptionClick(option)}
                  >
                    {labelText}
                  </li>
                );
              })}
            </ul>
          )}

          {error && <div className="combobox-error-text">{error}</div>}

        </div>
      </FormGroup>
    );
  }
}

export default ComboBoxField;
